import {Component} from '@angular/core';
import {UntypedFormGroup} from '@angular/forms';
import {MatFormFieldModule} from '@angular/material/form-field';
import {CategoryFilter} from '@app/search/filters/resources/category.filter';
import {CategoryService} from '@middleware';
import {IconTypeID} from '@middleware/models';
import {IconService} from '@middleware/services/icon/icon.service';
import {CustomFieldTypes, FormlyFieldTypes, HTMLInputFieldTypes} from '@models/form-field';
import {FieldType, FieldTypeConfig, FormlyFieldConfig, FormlyFormOptions} from '@ngx-formly/core';
import {phoneNumberOnInit} from '@validators/phone-number.validator';
import {map} from 'rxjs';

interface ResourceDetailsOptions {
  modelType: 'Generic' | 'Event';
  model?: any;
  creatorOrganization: string;
  categoryService?: CategoryService;
  iconService: IconService;
  categoryFilter: CategoryFilter;
}

@Component({
  imports: [MatFormFieldModule],
  template: `<div class="form-field standard">
    <mat-label class="form-field standard required">{{ props.label }}</mat-label>
    <span [class]="'display-field-value'" [innerHTML]="props['value']()"></span>
    <input type="hidden" [value]="props['value']()" class="cdk-visually-hidden" />
  </div>`,
})
export class TextDisplayField extends FieldType<FieldTypeConfig> {}

// Builds the Formly field config for resource details
export function buildResourceDetailsFields({
  modelType,
  creatorOrganization,
  model = {contacts: [{}]},
  categoryService,
  iconService,
  categoryFilter,
}: ResourceDetailsOptions): FormlyFieldConfig[] {
  const resourceIconOptions = buildResourceIconOptions(iconService);

  return [
    {
      key: 'name',
      type: FormlyFieldTypes.INPUT,
      className: 'wide-form-field',
      props: {
        label: 'Title',
        required: true,
        type: HTMLInputFieldTypes.TEXT,
      },
    },
    {
      key: 'icon_id',
      type: FormlyFieldTypes.SELECT,
      className: 'auto-width-field',
      props: {
        label: 'Icon',
        required: true,
        options: resourceIconOptions,
      },
    },
    {
      type: TextDisplayField,
      props: {
        label: 'Creator Organization',
        value: () => creatorOrganization,
      },
    },
    {
      key: 'show_creator_organization_logo',
      type: FormlyFieldTypes.CHECKBOX,
      defaultValue: true,
      props: {
        label: 'Show Creator Organization Logo',
      },
      wrappers: ['checkbox-wrapper'],
    },
    {
      key: 'location',
      type: FormlyFieldTypes.INPUT,
      className: 'wide-form-field',
      props: {
        label: 'Event Location',
        type: HTMLInputFieldTypes.TEXT,
      },
      expressions: {
        hide: () => modelType !== 'Event',
        'props.required': () => modelType === 'Event',
      },
    },
    {
      fieldGroupClassName: 'datetime-field',
      expressions: {
        hide: () => modelType !== 'Event',
      },
      fieldGroup: [
        {
          key: 'starts',
          type: FormlyFieldTypes.DATEPICKER,
          className: 'short-text-field left',
          props: {label: 'Event Start Date'},
          expressions: {
            'props.required': () => modelType === 'Event',
          },
        },
        {
          key: 'starts',
          type: CustomFieldTypes.TIMEPICKER,
          className: 'short-text-field',
          props: {label: 'Event Start Time'},
          expressions: {
            'props.required': () => modelType === 'Event',
          },
        },
      ],
    },
    {
      fieldGroupClassName: 'datetime-field',
      expressions: {
        hide: () => modelType !== 'Event',
      },
      fieldGroup: [
        {
          key: 'ends',
          type: FormlyFieldTypes.DATEPICKER,
          className: 'short-text-field left',
          props: {label: 'Event End Date'},
          validators: {
            validation: ['dateRange'],
          },
          expressions: {
            'props.required': () => modelType === 'Event',
          },
        },
        {
          key: 'ends',
          type: CustomFieldTypes.TIMEPICKER,
          className: 'short-text-field',
          props: {label: 'Event End Time'},
          expressions: {
            'props.required': () => modelType === 'Event',
          },
        },
      ],
    },
    {
      key: 'description',
      type: CustomFieldTypes.RICH_TEXT,
      props: {label: 'Description', required: true},
    },
    {
      key: 'categories',
      type: CustomFieldTypes.CATEGORY_TREE,
      props: {
        label: 'Categories',
        required: true,
        categoryDataSignal: categoryFilter.catTree,
        placeholder: 'Select categories',
        outputMode: 'tertiaryOnly',
      },
      wrappers: ['category-tree-wrapper'],
    },
    {
      key: 'contacts',
      type: 'repeat',
      fieldArray: {
        fieldGroup: [
          {
            key: 'name',
            type: FormlyFieldTypes.INPUT,
            props: {
              label: 'Contact Name',
              type: 'name',
            },
          },
          {
            key: 'email',
            type: FormlyFieldTypes.INPUT,
            props: {
              label: 'Contact Email',
              type: HTMLInputFieldTypes.EMAIL,
            },
            validators: {validation: ['email']},
          },
          {
            key: 'phone_number',
            type: FormlyFieldTypes.INPUT,
            props: {
              label: 'Contact Phone',
              type: HTMLInputFieldTypes.TEL,
            },
            validators: {validation: ['phone-number']},
            hooks: {onInit: phoneNumberOnInit},
          },
        ],
      },
    },
    {
      key: 'website_url',
      type: FormlyFieldTypes.INPUT,
      className: 'wide-form-field',
      props: {
        label: 'Website',
        type: HTMLInputFieldTypes.URL,
        hint: 'Must start with https://',
      },
      validators: {validation: ['https-only']},
    },
    {
      key: 'spatial_coverage',
      props: {
        label: 'Address',
      },
      fieldGroup: [
        {
          key: 'street',
          type: FormlyFieldTypes.INPUT,
          className: 'wide-form-field',
          props: {
            label: 'Street Address',
            type: HTMLInputFieldTypes.TEXT,
          },
        },
        {
          key: 'city',
          type: FormlyFieldTypes.INPUT,
          className: 'wide-form-field',
          props: {
            label: 'City',
            type: HTMLInputFieldTypes.TEXT,
          },
        },
        {
          key: 'state',
          type: FormlyFieldTypes.INPUT,
          className: 'wide-form-field',
          props: {
            label: 'State',
            type: HTMLInputFieldTypes.TEXT,
          },
          validators: {validation: ['state-abbr']},
        },
        {
          key: 'zip_code',
          type: FormlyFieldTypes.INPUT,
          className: 'wide-form-field',
          props: {
            label: 'ZIP Code',
            type: HTMLInputFieldTypes.TEXT,
          },
          validators: {validation: ['zip-code']},
        },
        {
          key: 'country',
          type: FormlyFieldTypes.INPUT,
          className: 'wide-form-field',
          props: {
            label: 'Country',
            type: HTMLInputFieldTypes.TEXT,
          },
        },
      ],
    },
  ];
}

export function createFormGroup() {
  return new UntypedFormGroup({});
}

export function createFormlyOptions(): FormlyFormOptions {
  return {};
}

function buildResourceIconOptions(iconService: IconService) {
  return iconService.getIcons({type_id: IconTypeID.RESOURCE}).pipe(
    map(response =>
      response.icons // nosonar
        .sort((a, b) => a.display_order - b.display_order)
        .map(icon => ({label: icon.name, value: icon.id})),
    ),
  );
}
